// =============================================================================
// src/data/hasura-graphql-post.ts — Transporte compartilhado Hasura anon (POST).
//
// Extraído do sentinel-client (2026-07) para o Analytics reutilizar sem duplicar
// o contrato de erro/timeout: POST com header x-hasura-client-id (a role anon do
// Hasura filtra por session var; sem o header o GraphQL falha com "missing
// session variable"), AbortController com timeout e throw em HTTP != 2xx,
// `errors` do GraphQL ou resposta sem `data`. SRP: só transporte, sem mapeamento.
// =============================================================================

export interface HasuraGraphqlPostRequest {
  readonly endpoint: string;
  readonly clientId: string;
  readonly query: string;
  readonly variables: Record<string, unknown>;
  readonly fetchImpl: typeof fetch;
  readonly timeoutMs: number;
  /** Prefixo das mensagens de erro (ex.: 'Sentinel', 'Analytics') — preserva o contrato de erro por consumidor. */
  readonly errorLabel: string;
}

interface HasuraGraphqlResponse<TData> {
  readonly data?: TData;
  readonly errors?: readonly unknown[];
}

/**
 * POST GraphQL anon no Hasura com timeout e validação de resposta.
 *
 * @example const data = await postHasuraGraphql<SentinelGraphqlData>({ endpoint, clientId, query, variables, fetchImpl: fetch, timeoutMs: 8000, errorLabel: 'Sentinel' });
 */
export async function postHasuraGraphql<TData>(request: HasuraGraphqlPostRequest): Promise<TData> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), request.timeoutMs);
  // fetchImpl DESACOPLADO do request antes de chamar: `request.fetchImpl(...)`
  // seria chamada de método (this = request) e o fetch nativo do browser faz
  // brand-check do receiver → "Illegal invocation" (regressão real pega em
  // produção 2026-07-25; jsdom/vi.fn não reproduzem o brand-check).
  const { fetchImpl } = request;
  try {
    const res = await fetchImpl(request.endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-hasura-client-id': request.clientId },
      body: JSON.stringify({ query: request.query, variables: request.variables }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`${request.errorLabel} ${request.endpoint}: HTTP ${res.status}`);
    const json = (await res.json()) as HasuraGraphqlResponse<TData>;
    if (json.errors) throw new Error(`${request.errorLabel} GraphQL ${JSON.stringify(json.errors)}`);
    if (!json.data) throw new Error(`${request.errorLabel} ${request.endpoint}: resposta sem data`);
    return json.data;
  } finally {
    clearTimeout(timer);
  }
}
